import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  DEMO_LEASES, DEFAULT_ECON, SCENARIOS,
  computeEconWindow, computeBreakevenOil,
  generateReasoning, answerPrompt, PRESET_PROMPTS,
} from "./econEngine";
import "./EconModal.css";

/* ═══════════════════════════════════════════════════════════════════════
 * EconModal — Premium Economic Simulation & Reasoning Modal
 * 3-zone layout: Assumptions | Metrics | Operator Reasoning
 * Fully reactive: all outputs update live as assumptions change
 * ═══════════════════════════════════════════════════════════════════════ */

const moneyFmt = (n, d = 0) => {
  if (!Number.isFinite(n)) return "\u2014";
  return new Intl.NumberFormat(undefined, {
    style: "currency", currency: "USD", maximumFractionDigits: d,
  }).format(n);
};

export default function EconModal({ open, onClose, initialLeaseIdx = 0, onLeaseChange, realLease = null }) {
  const [leaseIdx, setLeaseIdx] = useState(initialLeaseIdx);
  const [econ, setEcon] = useState(null); // null = use lease defaults
  const [scenarioKey, setScenarioKey] = useState("Base");
  const [promptAnswer, setPromptAnswer] = useState(null);

  // Sync with parent lease selection
  useEffect(() => { setLeaseIdx(initialLeaseIdx); }, [initialLeaseIdx]);

  // realLease (live LCS overlay) takes priority over the demo-leases index.
  const lease = realLease || DEMO_LEASES[leaseIdx];
  const isRealLcs = Boolean(realLease && realLease.isRealLcs);
  // Use lease-specific defaults, overlaid with user edits.
  const effectiveEcon = econ || lease.econDefaults || DEFAULT_ECON;
  const scenario = SCENARIOS.find(s => s.key === scenarioKey) || SCENARIOS[0];

  const econResult = useMemo(
    () => computeEconWindow(lease, effectiveEcon, scenario),
    [lease, effectiveEcon, scenario]
  );

  const breakeven = useMemo(
    () => computeBreakevenOil(lease, effectiveEcon, scenario),
    [lease, effectiveEcon, scenario]
  );

  const reasoning = useMemo(
    () => generateReasoning(econResult, lease, effectiveEcon, DEFAULT_ECON),
    [econResult, lease, effectiveEcon]
  );

  const scenarioResults = useMemo(
    () => SCENARIOS.map(sc => ({ ...sc, result: computeEconWindow(lease, effectiveEcon, sc) })),
    [lease, effectiveEcon]
  );

  // Decision shift detection
  const prevDecisionRef = useRef(econResult.decision);
  const [decisionShift, setDecisionShift] = useState(false);
  useEffect(() => {
    if (prevDecisionRef.current && prevDecisionRef.current !== econResult.decision) {
      setDecisionShift(true);
      const t = setTimeout(() => setDecisionShift(false), 600);
      return () => clearTimeout(t);
    }
    prevDecisionRef.current = econResult.decision;
  }, [econResult.decision]);

  const setField = useCallback((field, value) => {
    setEcon(prev => ({ ...(prev || lease.econDefaults), [field]: value }));
    setPromptAnswer(null);
  }, [lease]);

  const handlePrompt = useCallback((key) => {
    setPromptAnswer({ key, answer: answerPrompt(key, econResult, lease, effectiveEcon) });
  }, [econResult, lease, effectiveEcon]);

  const handleLeaseChange = useCallback((idx) => {
    setLeaseIdx(idx);
    setEcon(null); // Reset to lease defaults
    setPromptAnswer(null);
    onLeaseChange?.(idx);
  }, [onLeaseChange]);

  if (!open) return null;

  const r = econResult;
  const decColor = r.decision === "Shut-in candidate" ? "#e85555"
    : r.decision === "Monitor" ? "#c89030" : "#40b878";
  const confColor = r.confidence === "High" ? "#40b878"
    : r.confidence === "Medium" ? "#c89030" : "#e85555";

  return (
    <div className="em-overlay" onClick={onClose}>
      <div className="em-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="em-header">
          <div className="em-header-l">
            <div className="em-header-title">ECONOMIC SIMULATION</div>
            <div className="em-header-sub">
              {lease.name} &bull; {lease.basin} &bull; {lease.operator}
            </div>
          </div>
          <div className="em-header-r">
            {isRealLcs ? (
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                padding: "4px 8px", borderRadius: 2, color: "#22d3ee",
                background: "rgba(34,211,238,0.12)", border: "1px solid #22d3ee",
                fontFamily: "monospace", marginRight: 8,
              }}>LIVE LCS &middot; {lease.id}</span>
            ) : (
              <select
                className="em-lease-sel"
                value={leaseIdx}
                onChange={e => handleLeaseChange(Number(e.target.value))}
              >
                {DEMO_LEASES.map((l, i) => (
                  <option key={l.id} value={i}>{l.name} \u2014 {l.basin}</option>
                ))}
              </select>
            )}
            <button className="em-close" onClick={onClose}>{"\u2715"}</button>
          </div>
        </div>

        {/* Body: 3-zone */}
        <div className="em-body">

          {/* ═══ LEFT: Control Surface ═══ */}
          <div className="em-left">
            {/* Honest assumptions banner — shown for both demo + live LCS,
                 but only flagged loudly for live LCS leases (per-lease cost
                 deck not yet provided). */}
            {isRealLcs && (
              <div style={{
                fontSize: 10,
                fontFamily: "'JetBrains Mono','SF Mono',Consolas,monospace",
                color: "#fbbf24",
                background: "rgba(251,191,36,0.10)",
                border: "1px solid rgba(251,191,36,0.35)",
                borderRadius: 2,
                padding: "6px 8px",
                marginBottom: 8,
                lineHeight: 1.45,
              }}>
                <b>Default cost deck.</b> Per-lease LOE / water-disposal /
                ownership not loaded for {lease.id}. Edit any field below to
                model true operator costs. Use <b>Bear / Base / Bull</b> for a
                quick price sensitivity.
              </div>
            )}

            <div className="em-scenario-row">
              {SCENARIOS.map(sc => (
                <button
                  key={sc.key}
                  className={`em-sc-btn ${scenarioKey === sc.key ? "em-sc-active" : ""}`}
                  onClick={() => setScenarioKey(sc.key)}
                >{sc.key}</button>
              ))}
            </div>

            <div className="em-group">
              <div className="em-grp-title">PRICING</div>
              <EconInput label="Oil $/bbl" value={effectiveEcon.oilPrice} onChange={v => setField("oilPrice", v)} step={1} primary />
              <EconInput label="Gas $/mcf" value={effectiveEcon.gasPrice} onChange={v => setField("gasPrice", v)} step={0.1} primary />
              <EconInput label="Oil diff" value={effectiveEcon.oilDiff} onChange={v => setField("oilDiff", v)} step={0.5} />
              <EconInput label="Gas diff" value={effectiveEcon.gasDiff} onChange={v => setField("gasDiff", v)} step={0.1} />
              <EconInput label="Shrink %" value={effectiveEcon.gasShrink} onChange={v => setField("gasShrink", v)} step={0.01} />
            </div>

            <div className="em-group">
              <div className="em-grp-title">OWNERSHIP</div>
              <EconInput label="Royalty" value={effectiveEcon.royalty} onChange={v => setField("royalty", v)} step={0.01} />
              <EconInput label="WI" value={effectiveEcon.workingInterest} onChange={v => setField("workingInterest", v)} step={0.01} />
              <EconInput label="NRI override" value={effectiveEcon.nriOverride} onChange={v => setField("nriOverride", v)} step={0.01} />
              <EconInput label="Oil sev tax" value={effectiveEcon.oilSevTax} onChange={v => setField("oilSevTax", v)} step={0.005} />
              <EconInput label="Gas sev tax" value={effectiveEcon.gasSevTax} onChange={v => setField("gasSevTax", v)} step={0.005} />
              <EconInput label="Ad valorem" value={effectiveEcon.adValTax} onChange={v => setField("adValTax", v)} step={0.005} />
            </div>

            <div className="em-group">
              <div className="em-grp-title">OPERATING COSTS</div>
              <EconInput label="Fixed LOE $/mo" value={effectiveEcon.fixedLoe} onChange={v => setField("fixedLoe", v)} step={100} primary />
              <EconInput label="Var LOE $/boe" value={effectiveEcon.varLoe} onChange={v => setField("varLoe", v)} step={0.25} />
              <EconInput label="Water disp $/bbl" value={effectiveEcon.waterDisp} onChange={v => setField("waterDisp", v)} step={0.25} primary />
              <EconInput label="Chemicals $/boe" value={effectiveEcon.chemicals} onChange={v => setField("chemicals", v)} step={0.1} />
              <EconInput label="Electric $/boe" value={effectiveEcon.electricity} onChange={v => setField("electricity", v)} step={0.1} />
              <EconInput label="Overhead $/mo" value={effectiveEcon.overhead} onChange={v => setField("overhead", v)} step={100} />
              <EconInput label="Base maint $/mo" value={effectiveEcon.baseMaint} onChange={v => setField("baseMaint", v)} step={100} />
            </div>

            <div className="em-group">
              <div className="em-grp-title">CAPITAL & OPS</div>
              <EconInput label="Capex $" value={effectiveEcon.capex} onChange={v => setField("capex", v)} step={1000} />
              <EconInput label="Abandonment $" value={effectiveEcon.abandonmentCost} onChange={v => setField("abandonmentCost", v)} step={500} />
              <EconInput label="Downtime %" value={effectiveEcon.downtimePct} onChange={v => setField("downtimePct", v)} step={0.01} />
              <EconInput label="Discount rate" value={effectiveEcon.discountRate} onChange={v => setField("discountRate", v)} step={0.01} />
              <EconInput label="Window (days)" value={effectiveEcon.windowDays} onChange={v => setField("windowDays", v)} step={5} />
            </div>

            <button className="em-reset-btn" onClick={() => { setEcon(null); setPromptAnswer(null); }}>
              RESET DEFAULTS
            </button>
          </div>

          {/* ═══ CENTER: Metrics ═══ */}
          <div className="em-center">

            {/* Decision card */}
            <div className={`em-decision-card ${decisionShift ? "em-dec-shift" : ""}`}>
              <div className="em-dec-label">RECOMMENDATION</div>
              <div className="em-dec-value em-dec-glow" style={{ color: decColor }}>
                {r.decision.toUpperCase()}
              </div>
              {decisionShift && <div className="em-shift-notice">Recommendation changed</div>}
              <div className="em-dec-detail">{r.decisionDetail}</div>
              <div className="em-dec-confidence">
                Confidence: <span style={{ color: confColor }}>{r.confidence}</span>
              </div>
            </div>

            {/* KPI — primary */}
            <div className="em-kpi-primary">
              <KPI label="PV (discounted)" value={moneyFmt(r.cumPV)} color={r.cumPV >= 0 ? "#40b878" : "#e85555"} large />
              <KPI label="Cumulative CF" value={moneyFmt(r.cumCF)} color={r.cumCF >= 0 ? "#40b878" : "#e85555"} large />
            </div>
            {/* KPI — secondary */}
            <div className="em-kpi-secondary">
              <KPI label="Breakeven" value={breakeven != null ? `$${breakeven.toFixed(0)}` : "\u2014"} />
              <KPI label="Netback" value={moneyFmt(r.netback, 2)} color={r.netback >= 0 ? "#40b878" : "#e85555"} />
              <KPI label="1st Neg CF" value={r.firstNeg != null ? `d${r.firstNeg}` : "\u2014"} color={r.firstNeg != null && r.firstNeg < 30 ? "#e85555" : undefined} />
              <KPI label="Payout" value={r.payoutDay != null ? `d${r.payoutDay}` : "\u2014"} />
              <KPI label="Econ Limit" value={r.econLimit != null ? `d${r.econLimit}` : "\u2014"} color={r.econLimit != null ? "#c89030" : undefined} />
              <KPI label="LOE/boe" value={moneyFmt(r.loePerBoe, 2)} />
            </div>

            {/* What matters */}
            <div className="em-what-matters">
              <div className="em-sec-title">WHAT MATTERS</div>
              <div className="em-wm-text">{lease.inference.whatMatters}</div>
            </div>

            {/* Scenario comparison */}
            <div className="em-scenarios">
              <div className="em-sec-title">SCENARIO COMPARISON</div>
              <table className="em-sc-table">
                <thead>
                  <tr>
                    <th>Scenario</th>
                    <th>PV</th>
                    <th>Cum CF</th>
                    <th>Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {scenarioResults.map(sc => {
                    const sr = sc.result;
                    const dc = sr.decision === "Operate" ? "g"
                      : sr.decision === "Monitor" ? "y" : "r";
                    return (
                      <tr key={sc.key} className={sc.key === scenarioKey ? "em-sc-sel" : ""}>
                        <td className="em-sc-name">{sc.key}</td>
                        <td style={{ color: sr.cumPV >= 0 ? "#40b878" : "#e85555" }}>
                          {moneyFmt(sr.cumPV)}
                        </td>
                        <td style={{ color: sr.cumCF >= 0 ? "#40b878" : "#e85555" }}>
                          {moneyFmt(sr.cumCF)}
                        </td>
                        <td>
                          <span className={`em-dec-pill em-dec-${dc}`}>
                            {sr.decision}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Stream confidence */}
            <div className="em-conf-strip">
              <div className="em-sec-title">STREAM CONFIDENCE</div>
              <div className="em-conf-row">
                {Object.entries(lease.inference.confidence).map(([k, v]) => (
                  <span key={k} className={`em-conf-chip em-conf-${v.toLowerCase()}`}>
                    {k}: {v}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ═══ RIGHT: Reasoning ═══ */}
          <div className="em-right">
            <div className="em-sec-title">OPERATOR REASONING</div>

            <div className="em-reasoning-feed">
              {reasoning.map((msg, i) => (
                <div key={i} className={`em-msg em-msg-${msg.type}`}>
                  <span className="em-msg-icon">
                    {msg.type === "insight" ? "\u25C6"
                      : msg.type === "takeaway" ? "\u25C9" : "\u25B2"}
                  </span>
                  <span className="em-msg-text">{msg.text}</span>
                </div>
              ))}
            </div>

            {/* Prompt answer */}
            {promptAnswer && (
              <div className="em-prompt-answer">
                <div className="em-pa-q">
                  {PRESET_PROMPTS.find(p => p.key === promptAnswer.key)?.label || ""}
                </div>
                <div className="em-pa-a">{promptAnswer.answer}</div>
              </div>
            )}

            {/* Preset prompts */}
            <div className="em-prompts">
              <div className="em-prompts-label">ASK</div>
              {PRESET_PROMPTS.map(p => (
                <button
                  key={p.key}
                  className="em-prompt-btn"
                  onClick={() => handlePrompt(p.key)}
                >{p.label}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Sub-components ─── */
function EconInput({ label, value, onChange, step = 1, min, max, primary }) {
  return (
    <div className={`em-input-row ${primary ? "em-input-primary" : ""}`}>
      <span className="em-input-label">{label}</span>
      <input
        className="em-input"
        type="number"
        value={value}
        step={step}
        min={min}
        max={max}
        onChange={e => {
          const v = parseFloat(e.target.value);
          onChange(Number.isFinite(v) ? v : 0);
        }}
      />
    </div>
  );
}

function KPI({ label, value, color, large }) {
  return (
    <div className={`em-kpi ${large ? "em-kpi-lg" : ""}`}>
      <div className="em-kpi-label">{label}</div>
      <div className="em-kpi-value" style={color ? { color } : undefined}>
        {value}
      </div>
    </div>
  );
}
